/* Tracking Calendar Data Service
 *
 * Angular Module: trackerbase-app.services.tracking-calendar-data-service
 * Injected Service: TrackingCalendarData
 */

import angular from "angular";
import moment from "moment-timezone";

import Assert from "tbAssert";

import Recipient from "../models/Recipient";
import TrackingCalendarDataOperationError from "../models/errors/TrackingCalendarDataOperationError";
//import TrackingCalendarDataOperationCancelledError from "../models/errors/TrackingCalendarDataOperationCancelledError";

import TbAppConfigDataServiceModule from "../config/tb-app-config/TbAppConfigDataService";
import TrackingEventFactoriesModule from "../models/factories/tracking-event-factories";
import CurrentTimeZoneModule from "./CurrentTimeZoneService";
import TrackingCalendarOptionsModule from "./TrackingCalendarOptionsService";
import ServerFilterProcessorModule from "./ServerFilterProcessorService";
import ServerSortProcessorModule from "./ServerSortProcessorService";

const mHttpSvc = new WeakMap();
const mQSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mTrackingEventFactorySvc = new WeakMap();
const mCurrentTimeZoneSvc = new WeakMap();
const mTrackingCalendarOptionsSvc = new WeakMap();
const mServerFilterProcessorSvc = new WeakMap();
const mServerSortProcessorSvc = new WeakMap();

const mTbServerBaseUrl = new WeakMap();
const mEventReportsDataUrlBase = new WeakMap();

const defaultDeleteResultStatus = -1;
const deleteEventResultsTable = new Map([
    [200, {
        result: true,
        reason: null
    }],
    [204, {
        result: true,
        reason: null
    }],
    [404, {
        result: false,
        reason: "An event with id could not be found. It may have already been deleted."
    }],
    [403, {
        result: false,
        reason: "You do not have the necessary permissions to delete this event."
    }],
    [409, {
        result: false,
        reason: "The event was updated by another user while your delete request was being processed. Please try deleting the event again after refreshing the event editor."
    }],
    [defaultDeleteResultStatus, {
        result: false,
        reason: "Unable to delete event because of an unexpected error. Please contact TrackerBase support for further assistance."
    }]
]);

class TrackingCalendarDataService {
    constructor(
        $http,
        $q,
        $log,
        $timeout,
        TbAppConfigDataService,
        TrackingEventFactory,
        CurrentTimeZone,
        TrackingCalendarOptions,
        ServerFilterProcessor,
        ServerSortProcessor
    ) {

        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");

        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");
        Assert.StrictAssert(TrackingEventFactory != null, "Assertion Failure: Missing Tracking Event Factory Dependency");
        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone Service Dependency");
        Assert.StrictAssert(TrackingCalendarOptions != null, "Assertion Failure: Missing Tracking Calendar Options Service Dependency");
        Assert.StrictAssert(ServerFilterProcessor != null, "Assertion Failure: Missing Server Filter Processor Service Dependency");
        Assert.StrictAssert(ServerSortProcessor != null, "Assertion Failure: Missing Server Sort Processor Service Dependency");

        mHttpSvc.set(this, $http);
        mQSvc.set(this, $q);
        mLogSvc.set(this, $log);
        mTimeoutSvc.set(this, $timeout);

        const tbServerBaseUrl = TbAppConfigDataService.tbServerBaseUrl;
        mTbServerBaseUrl.set(this, tbServerBaseUrl);
        mEventReportsDataUrlBase.set(this, `${tbServerBaseUrl}/api/trackingevents/reportdata`);

        mTrackingEventFactorySvc.set(this, TrackingEventFactory);
        mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        mTrackingCalendarOptionsSvc.set(this, TrackingCalendarOptions);
        mServerFilterProcessorSvc.set(this, ServerFilterProcessor);
        mServerSortProcessorSvc.set(this, ServerSortProcessor);
    }

    _processEventsReportedDataResponse(response) {
        let serverResponseData = response.data;

        let reportedEventsData = [];

        Object.keys(serverResponseData).forEach((officeTypeName) => {
            reportedEventsData.push({
                officeTypeName: officeTypeName,
                reportedEvents: serverResponseData[officeTypeName].map(
                    (serverEvent) => mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEvent)
                )
            });
        });

        return reportedEventsData;
    }

    _getViewParams(calendarViewName) {
        let viewTypeFn = null;
        switch(calendarViewName) {
            case "day":
                viewTypeFn = (targetDate) => {
                    return {
                        apiUrl: `${mTbServerBaseUrl.get(this)}/api/trackingevents/calendar/${targetDate.getFullYear()}/${targetDate.getMonth() + 1}/${targetDate.getDate()}`,
                        apiParams: {}
                    };
                };
                break;
            case "threeday":
                viewTypeFn = (targetDate) => {
                    return {
                        apiUrl: `${mTbServerBaseUrl.get(this)}/api/trackingevents/calendar/${targetDate.getFullYear()}/${targetDate.getMonth() + 1}/${targetDate.getDate()}/threeday`,
                        apiParams: {}
                    };
                };
                break;
            case "week":
                viewTypeFn = (targetDate) => {
                    return {
                        apiUrl: `${mTbServerBaseUrl.get(this)}/api/trackingevents/calendar/${targetDate.getFullYear()}/${targetDate.getMonth() + 1}/${targetDate.getDate()}/weekof`,
                        apiParams: {}
                    };
                };
                break;
            case "month":
                viewTypeFn = (targetDate) => {
                    return {
                        apiUrl: `${mTbServerBaseUrl.get(this)}/api/trackingevents/calendar/${targetDate.getFullYear()}/${targetDate.getMonth() + 1}`,
                        apiParams: {}
                    };
                };
                break;
            case "agenda":
                viewTypeFn = (targetDate) => {

                    let agendaDays = mTrackingCalendarOptionsSvc.get(this).AgendaDaysToShowPref;

                    let agendaStart = moment(targetDate);
                    let agendaEnd = agendaStart.clone().add(agendaDays, "days");

                    return {
                        apiUrl: `${mTbServerBaseUrl.get(this)}/api/trackingevents/daterange`,
                        apiParams: {
                            startInstant: agendaStart.toISOString(),
                            endInstant: agendaEnd.toISOString()
                        }
                    };
                };
                break;
            default:
                throw new Error("Invalid Calendar View Type");
        }

        return viewTypeFn;
    }

    queryCalendar(options, viewName, targetDate, localTz) {
        let apiParameters = this._getViewParams(viewName)(targetDate);
        let queryParams = {
            method: "GET",
            url: apiParameters.apiUrl,
            params: angular.extend(apiParameters.apiParams, { timeZoneId: localTz })
        };

        if (options.data["filter"] != null) {
            let filterParam = mServerFilterProcessorSvc.get(this).ProcessFilterForServer(options.data.filter, "||");
            queryParams.params["q"] = filterParam;
        }

        return mHttpSvc.get(this)(queryParams)
            .then(
                (response) => {
                    let responseData = response.data;
                    if (responseData["Events"] != null) {
                        let eventResponse = [];
                        let responseEvents = responseData.Events;
                        responseEvents.forEach((serverEvent) => {
                            let receivedEvent =
                                mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEvent);
                            let schedulerEvent =
                                receivedEvent.toSchedulerEventModel();
                            eventResponse.push(schedulerEvent);
                        });
                        return eventResponse;
                    }
                    else {
                        //TODO: See If Returning A Promise Rejection Breaks Calendar When No Events Are In Requested Time Period.
                        return undefined;
                    }
                },
                (errorResponse) => mQSvc.get(this).reject(errorResponse)
            );
    }

    queryEvents(pageNumber, pageSize, filter, eventSort) {
        let trackingEventQueryApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents`;
        let eventQueryParams = {};

        if ((Number.isInteger(pageNumber)) &&
            (pageNumber > 0))
        {
            eventQueryParams.pageNumber = pageNumber;
        }

        if ((Number.isInteger(pageSize)) &&
            (pageSize > 0))
        {
            eventQueryParams.pageSize = pageSize;
        }

        if (filter != null) {
            let filterParam = mServerFilterProcessorSvc.get(this).ProcessFilterForServer(filter, "||");
            eventQueryParams["q"] = filterParam;
        }

        if (eventSort != null)
        {
            let sortParam = mServerSortProcessorSvc.get(this).ProcessEventSort(eventSort);
            if (sortParam != null) {
                eventQueryParams["sort"] = sortParam;
            }
        }

        return mHttpSvc.get(this)({
            method: "GET",
            url: trackingEventQueryApiUrl,
            params: eventQueryParams
        })
        .then(
            (response) => {
                let responseData = response.data;
                if (responseData["Items"] != null) {
                    let responseEvents = responseData.Items;
                    let processedEvents = responseEvents.map(
                        (serverEvent) => mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEvent)
                    );

                    return {
                        totalItems: responseData.TotalItems,
                        eventData: processedEvents
                    };
                }
                else {
                    //TODO: See If Returning A Promise Rejection Breaks Calendar When No Events Are In Requested Time Period.
                    return undefined;
                }
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    getEvent(eventId) {

        let getEventOpName = "getEvent";
        let trackingEventApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${eventId}`;

        return mHttpSvc.get(this)({
            method: "GET",
            url: trackingEventApiUrl
        })
        .then((response) => {
            let serverEventData = response.data;
            let trackingEvent =
                mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEventData);

            return trackingEvent;
        })
        .catch((errorResponse) => {
            if (errorResponse == null) {
                throw new TrackingCalendarDataOperationError({
                   currentOperation: getEventOpName,
                   errorMessage: "No data received from server. Please check your internet connection and try again.",
                   errorData: null
                });
            }
            if (errorResponse instanceof Error) {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: getEventOpName,
                    errorMessage: errorResponse.message,
                    errorData: errorResponse
                });
            }
            else if (errorResponse.status === 404) {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: getEventOpName,
                    errorMessage: "Event not found. It may have been deleted or had its permissions changed recently.",
                    errorData: errorResponse
                });
            }
            //TODO: Handle 401/403 by redirecting to the login page, rather than as just another error.
            else if (errorResponse.status === 401 || errorResponse.status === 403) {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: getEventOpName,
                    errorMessage: "You do not have permission to access this event. Please reload this page in your browser to try and login again.",
                    errorData: errorResponse
                });
            }
            else if (errorResponse["statusText"] != null) {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: getEventOpName,
                    errorMessage: `Unable to load event: ${errorResponse.statusText}.`,
                    errorData: errorResponse
                });
            }
            else {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: getEventOpName,
                    errorMessage: "Unable to load event due to an unknown error.",
                    errorData: errorResponse
                });
            }
       });
    }

    createEvent(trackingEvent) {
        const createEventOpName = "createEvent";

        if (trackingEvent.EventId !== 0) {
            throw new TrackingCalendarDataOperationError({
                currentOperation: createEventOpName,
                errorMessage: "Create event has invalid event id",
                errorData: {
                    badEventId: trackingEvent.EventId
                }
            });
        }

        let serverCreateEventRequest =
            trackingEvent.toServerCreateEventRequest();

        let trackingEventCreateApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents`;

        return mHttpSvc.get(this)({
            method: "POST",
            url: trackingEventCreateApiUrl,
            data: serverCreateEventRequest
        })
        .then(
            (response) => {
                if ((response.status === 201) || (response.status === 200)) {
                    let createdServerEvent = response.data;
                    let createdEvent = mTrackingEventFactorySvc.get(this).ParseServerEvent(createdServerEvent);
                    return createdEvent;
                }
                else {
                    //TODO: See If Returning A Promise Rejection Here Breaks Event Creation Pipeline.
                    return undefined;
                }
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    updateEventFromCalendar(options) {
        let updateRequestEvent =
            mTrackingEventFactorySvc.get(this).ParseSchedulerEventData(options.data);

        return this.updateEvent(updateRequestEvent)
        .then(
            (updatedEvent) => updatedEvent.toSchedulerEventModel(),
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    updateEvent(trackingEvent) {
        let serverUpdateRequest =
            trackingEvent.toServerUpdateEventRequest();

        let trackingEventUpdateApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEvent.EventId}`;

        return mHttpSvc.get(this)({
            method: "PATCH",
            url: trackingEventUpdateApiUrl,
            data: serverUpdateRequest
        })
        .then(
            (response) => {
                let updatedServerEvent = response.data;
                let updatedEvent =
                    mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                return updatedEvent;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    deleteEvent(trackingEventId) {

        const deleteOperationName = "deleteEvent";
        const logSvc = mLogSvc.get(this);
        //logSvc.debug(deleteEventResultsTable);

        if (!(Number.isInteger(trackingEventId) && (trackingEventId > 0))) {
            return Promise.resolve({}).then(() => {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: deleteOperationName,
                    errorMessage: "Invalid Tracking Event Id",
                    errorData: trackingEventId
                });
            });
        }

        let trackingEventDeleteApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}`;

        return mHttpSvc.get(this)({
            method: "DELETE",
            url: trackingEventDeleteApiUrl
        })
        .then(
            (response) => {
                if (response == null) {
                    throw new TrackingCalendarDataOperationError({
                        currentOperation: deleteOperationName,
                        errorMessage: "No response or invalid response data",
                        errorData: null
                    });
                }

                let responseStatusResult = deleteEventResultsTable.get(response.status);
                if (responseStatusResult != null) {
                    if (responseStatusResult.result === true) {
                        return responseStatusResult;
                    }
                    else {
                        throw new TrackingCalendarDataOperationError({
                            currentOperation: deleteOperationName,
                            errorMessage: responseStatusResult.reason,
                            errorData: response
                        });
                    }
                }
                else {
                    let defErrorResult = deleteEventResultsTable.get(defaultDeleteResultStatus);
                    throw new TrackingCalendarDataOperationError({
                        currentOperation: deleteOperationName,
                        errorMessage: defErrorResult.reason,
                        errorData: response
                    });
                }
            }
        )
        .catch((errorResponse) => {
            if (errorResponse instanceof TrackingCalendarDataOperationError) {
                // we've already parsed the error.
                throw errorResponse;
            }
            else if (errorResponse instanceof Error) {
                // Process other exceptions into our known one.
                logSvc.debug("Caught unexpected exception in delete operation", errorResponse);
                throw new TrackingCalendarDataOperationError({
                    currentOperation: deleteOperationName,
                    errorMessage: `Caught unexpected exception in delete operation: ${errorResponse.message}`,
                    errorData: errorResponse
                });
            }
            else if (errorResponse == null) {
                throw new TrackingCalendarDataOperationError({
                    currentOperation: deleteOperationName,
                    errorMessage: "No response or invalid response data",
                    errorData: null
                });
            }
            else {
                let responseStatusResult = deleteEventResultsTable.get(errorResponse.status);

                if (responseStatusResult != null) {
                    if (responseStatusResult.result === true) {
                        // For some reason the error handler has been invoked despite a success-indicating response.
                        // We log a note, but divert back to the success path.
                        logSvc.info("Error handler invoked for success result.");
                        return responseStatusResult;
                    }
                    else {
                        throw new TrackingCalendarDataOperationError({
                            currentOperation: deleteOperationName,
                            errorMessage: responseStatusResult.reason,
                            errorData: errorResponse
                        });
                    }
                }
                else {
                    let defErrorResult = deleteEventResultsTable.get(defaultDeleteResultStatus);
                    throw new TrackingCalendarDataOperationError({
                        currentOperation: deleteOperationName,
                        errorMessage: defErrorResult.reason,
                        errorData: errorResponse
                    });
                }
            }
        });
    }

    updateEventAttendees(trackingEventId, updatedAttendees) {
        let updatedAttendeeIds =
            updatedAttendees.map((tgt) => tgt.trackingTargetId);

        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("trackingEventId must be an integer");
        }

        let eventAttendeesApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/attendees`;

        return mHttpSvc.get(this)({
            method: "PUT",
            url: eventAttendeesApiUrl,
            data: updatedAttendeeIds
        }).then(
            (response) => {
                let updatedServerEvent = response.data;
                let updatedEvent =
                    mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                return updatedEvent;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    updateEventTrackers(trackingEventId, updatedAssignedTrackers) {
        let assignedTrackerIds =
            updatedAssignedTrackers.map((tracker) => tracker.trackerId);

        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("tracking event id must be a number");
        }

        let eventTrackerApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/trackers`;

        return mHttpSvc.get(this)({
            method: "PUT",
            url: eventTrackerApiUrl,
            data: assignedTrackerIds
        })
        .then(
            (response) => {
                let updatedServerEvent = response.data;
                let updatedEvent =
                    mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                return updatedEvent;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    updateEventTypes(trackingEventId, updatedEventTypes) {
        let selectedEventTypeIds =
            updatedEventTypes.map((eventType) => eventType.eventTypeId);

        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("tracking event id must be a number");
        }

        let eventTypeApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/eventtypes`;

        return mHttpSvc.get(this)({
            method: "PUT",
            url: eventTypeApiUrl,
            data: selectedEventTypeIds
        })
        .then(
            (response) => {
                let updatedServerEvent = response.data;
                let updatedEvent =
                    mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                return updatedEvent;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    getEventSubscriberInfo(trackingEventId) {
        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("trackingEventId must be a number");
        }

        let eventSubscriberApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/subscribers`;

        return mHttpSvc.get(this)({
            method: "GET",
            url: eventSubscriberApiUrl,
            isArray: true
        })
        .then(
            (response) => {
                let serverData = response.data;
                let subscriptionData = serverData.map(function(svrRcpt) {
                    return Recipient.FromServerRecipient(svrRcpt);
                });
                return subscriptionData;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    preFileTrackingReport(trackingEventId, reportText, preFileTrackingStatus) {
        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("trackingEventId must be a number");
        }

        let preFileData = {};

        if ((reportText != null) &&
            (angular.isString(reportText)) &&
            (reportText.length > 0))
        {
            preFileData.ReportText = reportText;
        }
        else {
            return mQSvc.get(this).reject("Tracking Report Missing or Empty");
        }

        if ((preFileTrackingStatus != null) &&
            (preFileTrackingStatus.isReported === true)) {

            preFileData.PreFiledStatus = preFileTrackingStatus.trackingStatusId;
        }

        let preFileReportApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/trackingreport`;

        return mHttpSvc.get(this)({
            method: "POST",
            url: preFileReportApiUrl,
            data: preFileData,
            isArray: false
        })
        .then(
            (response) => {
                let updatedServerEvent = response.data;
                let updatedEvent =
                    mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                return updatedEvent;
            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    updateTrackingReport(trackingEventId, currentStatus, reportText, preFileTrackingStatus, notifySubscribers) {
        if (!Number.isInteger(trackingEventId)) {
            throw new TypeError("trackingEventId must be an integer");
        }

        let updateData = {};

        if ((reportText != null) &&
            (angular.isString(reportText)) &&
            (reportText.length > 0))
        {
            updateData.ReportText = reportText;
        }
        else {
            return mQSvc.get(this).reject("Tracking Report Missing or Empty");
        }

        if (currentStatus == null) {
            return mQSvc.get(this).reject("Current Status Missing or Null");
        }

        if (currentStatus.isApproved === true) {
            return mQSvc.get(this).reject("This event is approved but un-reported. Report the event to send a tracking report.");
        }

        updateData.PreFiledStatus = null;

        if (currentStatus.trackingStatusName === "Proposed")
        {
            if ((preFileTrackingStatus != null) &&
                (preFileTrackingStatus.isReported === true))
            {
                updateData.PreFiledStatus = preFileTrackingStatus.trackingStatusId;
            }
        }

        updateData.NotifySubscribersOfUpdate = null;
        if ((notifySubscribers != null) &&
            (typeof(notifySubscribers) === "boolean"))
        {
            updateData.NotifySubscribersOfUpdate = notifySubscribers;
        }

        let updateTrackingReportApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/${trackingEventId}/trackingreport`;

        return mHttpSvc.get(this)({
            method: "PUT",
            url: updateTrackingReportApiUrl,
            data: updateData,
            isArray: false
        })
        .then((response) => {
            let updatedServerEvent = response.data;
            let updatedEvent =
                mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

            return updatedEvent;
        });
        //TODO: Decide on error-handling strategy (rely on service clients or handle here and refactor?
        // Key Q: Does throwing in a catch handler here get handed down the caller's pipeline, or does it get swallowed?
    }

    _applyQueryFilterParamIfPresent(queryParams, filterQuery) {
        if (filterQuery != null) {
            if (angular.isString(filterQuery)) {
                queryParams.q = filterQuery;
            }
            else {
                // it's a kendo filter data structure, process it.
                const processedFilterQuery = mServerFilterProcessorSvc.get(this).ProcessFilterForServer(filterQuery, "||");
                queryParams.q = processedFilterQuery;
            }
        }
    }

    getEventsInDateRange(startMoment, endMoment, filterQuery) {

        if (!moment.isMoment(startMoment)) {
            throw new TypeError("Invalid start moment");
        }

        if (!moment.isMoment(endMoment)) {
            throw new TypeError("Invalid end moment");
        }

        let dateRangeQueryParams = {
            startInstant: startMoment.toISOString(),
            endInstant: endMoment.toISOString(),
            timeZoneId: "UTC"
        };

        this._applyQueryFilterParamIfPresent(dateRangeQueryParams, filterQuery);
        
        const eventsInDateRangeQueryApiUrl = `${mTbServerBaseUrl.get(this)}/api/trackingevents/daterange`;

        return mHttpSvc.get(this)({
            url: eventsInDateRangeQueryApiUrl,
            method: "GET",
            isArray: false,
            params: dateRangeQueryParams
        })
        .then(
            (response) => {

                let serverResponseData = response.data;

                return {
                    rangeStart: moment(serverResponseData.RangeStart).tz(serverResponseData.RangeTimeZone),
                    rangeEnd: moment(serverResponseData.RangeEnd).tz(serverResponseData.RangeTimeZone),
                    eventsInRange: serverResponseData.Events.map(
                        (serverEvent) => mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEvent)
                    ),
                    totalEvents: serverResponseData.PageElementCount
                };

            },
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    getEventsReportedToday(localTz, filterQuery) {

        let tzId = (localTz != null) ?
                    localTz :
                    mCurrentTimeZoneSvc.get(this).get();

        const eventsReportedTodayQueryApiUrl = `${mEventReportsDataUrlBase.get(this)}/tracked/today`;

        const reportQueryParams = { timeZoneId: tzId };
        this._applyQueryFilterParamIfPresent(reportQueryParams, filterQuery);

        return mHttpSvc.get(this)({
            url: eventsReportedTodayQueryApiUrl,
            method: "GET",
            isArray: false,
            params: reportQueryParams
        })
        .then(
            (response) => this._processEventsReportedDataResponse(response),
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    getEventsReportedOnDate(queryMoment, filterQuery) {

        let eventReportParams = {};

        if (moment.isMoment(queryMoment)) {
            eventReportParams.queryDate = queryMoment.toISOString();
            eventReportParams.timeZoneId = queryMoment.tz.zone.name;
            this._applyQueryFilterParamIfPresent(eventReportParams, filterQuery);
        }
        else {
            return this.getEventsReportedToday(null, filterQuery);
        }

        const eventsReportedOnDateQueryApiUrl = `${mEventReportsDataUrlBase.get(this)}/tracked/date`;

        return mHttpSvc.get(this)({
            url: eventsReportedOnDateQueryApiUrl,
            method: "GET",
            isArray: false,
            params: eventReportParams
        })
        .then(
            (response) => this._processEventsReportedDataResponse(response),
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    getEventsReportedInRange(startMoment, endMoment, filterQuery) {

        let eventReportParams = {};
        if ((moment.isMoment(startMoment)) &&
            (moment.isMoment(endMoment)))
        {
            eventReportParams.intervalStart = startMoment.toISOString();
            eventReportParams.intervalEnd = endMoment.toISOString();
            this._applyQueryFilterParamIfPresent(eventReportParams, filterQuery);
        }
        else {
            throw new TypeError("Invalid or missing start or end parameters.");
        }

        const eventsReportedInDateRangeQueryApiUrl = `${mEventReportsDataUrlBase.get(this)}/tracked`;

        return mHttpSvc.get(this)({
            url: eventsReportedInDateRangeQueryApiUrl,
            method: "GET",
            isArray: false,
            params: eventReportParams
        })
        .then(
            (response) => this._processEventsReportedDataResponse(response),
            (errorResponse) => mQSvc.get(this).reject(errorResponse)
        );
    }

    doWorkflowTransition(transitionToExecute, reportText) {

        if (transitionToExecute == null) {
            throw new Error("Missing required transition parameter");
        }

        let workflowTransitionDeferred = mQSvc.get(this).defer();

        mTimeoutSvc.get(this)(
            () => {
                let canMakeCall = false;
                let transitionApiUrl = transitionToExecute.apiUrl;

                let transitionHttpCallParams = {
                    method: "POST",
                    url: transitionApiUrl
                };

                if (transitionToExecute.isReportRequired) {

                    let transitionApiParams =
                        transitionToExecute.serverParams;

                    if ((reportText != null) &&
                        (angular.isString(reportText)) &&
                        (reportText.length > 0))
                    {
                        angular.extend(transitionApiParams, {
                            ReportBrief: reportText
                        });

                        transitionHttpCallParams.data = transitionApiParams;
                        canMakeCall = true;
                    }
                    else {
                        workflowTransitionDeferred.reject(
                            "Missing Required Report"
                        );
                        canMakeCall = false;
                    }
                }
                else {
                    canMakeCall = true;
                }

                if (canMakeCall === true) {
                    mHttpSvc.get(this)(
                        transitionHttpCallParams
                    )
                    .then(
                        (response) => {
                            let updatedServerEvent = response.data;
                            let updatedEvent =
                                mTrackingEventFactorySvc.get(this).ParseServerEvent(updatedServerEvent);

                            workflowTransitionDeferred.resolve(updatedEvent);
                        },
                        // TODO: Handle Failed Transition.
                        (response) => workflowTransitionDeferred.reject(response)
                    );
                }

            },
            10
        );

        return workflowTransitionDeferred.promise;
    }

}

TrackingCalendarDataService.$inject = [
    "$http",
    "$q",
    "$log",
    "$timeout",
    "TbAppConfigDataService",
    "TrackingEventFactory",
    "CurrentTimeZone",
    "TrackingCalendarOptions",
    "ServerFilterProcessor",
    "ServerSortProcessor"
];

export default angular
    .module("trackerbase-app.services.tracking-calendar-data-service", [
        TbAppConfigDataServiceModule,
        TrackingEventFactoriesModule,
        CurrentTimeZoneModule,
        TrackingCalendarOptionsModule,
        ServerFilterProcessorModule,
        ServerSortProcessorModule
    ])
    .service("TrackingCalendarData", TrackingCalendarDataService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/TrackingCalendarDataService.js
// module id = NdFI
// module chunks = 1